import { useCallback, useMemo, useState } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import { woocommerceStoreService } from "../services/woocommerceStoreService";
import type { WooProduct, WooStoreConfig } from "../types";

function mapProductsError(err: unknown): string {
  return err instanceof Error
    ? err.message
    : "Não foi possível carregar os produtos.";
}

/**
 * Produtos de uma loja WooCommerce + busca local (e via API quando recarrega).
 */
export function useWooCommerceProducts(store: WooStoreConfig | null) {
  const [searchTerm, setSearchTerm] = useState("");

  const loadTask = useCallback(
    async (search?: string) => {
      if (!store) return [] as WooProduct[];
      return woocommerceStoreService.listProducts(store, search);
    },
    [store],
  );

  const {
    run: loadProducts,
    loading,
    error,
    data: products,
    reset,
  } = useAsyncTask({
    task: loadTask,
    initialData: [] as WooProduct[],
    mapError: mapProductsError,
  });

  const filteredProducts = useMemo(() => {
    const list = products ?? [];
    if (!searchTerm.trim()) return list;
    return woocommerceStoreService.filterProductsLocally(list, searchTerm);
  }, [products, searchTerm]);

  const reload = useCallback(
    (apiSearch?: string) => {
      if (!store) return Promise.resolve([] as WooProduct[]);
      return loadProducts(apiSearch);
    },
    [store, loadProducts],
  );

  return {
    products: filteredProducts,
    allProducts: products ?? [],
    searchTerm,
    setSearchTerm,
    loadProducts: reload,
    loading,
    error: error ?? null,
    reset,
  };
}
